<template>
  <div class="mx-auto mb-3 flex max-w-[450px] flex-col rounded-xl border p-4 container:mx-0">
    <div class="flex pb-6">
      <ProductImage class="w-1/2 max-w-[120px] shrink-0" :product-media="product?.cover?.media" />
      <div class="flex flex-col justify-center gap-1 pl-4">
        <Tag v-if="subscriptionInterval" class="self-start" color="highlight">
          <CommonLabel path="BWF/shop/productDetailPage/subscriptionProduct/subscriptionLabel" />
        </Tag>
        <span class="block">{{ product?.translated?.name }}</span>

        <p v-if="firstTextOption && !isExchangeBox" class="block pb-1 text-sm">
          {{ firstTextOption?.name }}
        </p>

        <p v-if="!isExchangeBox" class="block pb-1 text-sm">
          <span v-if="subscriptionInterval">
            <CommonLabel
              path="BWF/shop/productDetailPage/subscriptionProduct/subscriptionIntervalDropdownLabel"
            />: {{ subscriptionInterval.interval }}&nbsp;{{ intervalUnitLabel }}
          </span>
          <CommonLabel
            v-else
            path="BWF/shop/productDetailPage/subscriptionProduct/singlePurchaseLabel"
          />
        </p>

        <div v-if="isExchangeBox" class="flex flex-col gap-2 text-sm">
          <CommonLabel
            class="block"
            path="BWF/shop/productDetailPage/crossSelling/exchangeboxSubline"
          />
          <div class="flex flex-col gap-1">
            <CommonLabel
              :placeholder="{ cylinderAmount }"
              path="BWF/shop/productDetailPage/crossSelling/exchangeboxCylinder"
            />
            <SharedPrice class="font-medium" :value="exchangeBoxPrice" />
          </div>
          <div class="flex flex-col gap-1">
            <CommonLabel
              :placeholder="{ cylinderAmount }"
              path="BWF/shop/productDetailPage/crossSelling/exchangeboxDeposit"
            />
            <SharedPrice class="font-medium" :value="totalDepositPrice" />
          </div>
        </div>
        <ProductCrossSellingPrice
          v-if="!isExchangeBox"
          :list-price="listPrice"
          :unit-price="unitPrice"
        />
      </div>
    </div>
    <AddToCartButton
      :product="product"
      class="btn btn-blue mt-auto w-full self-center"
      @close-cross-selling-modal="$emit('close-cross-selling-modal')"
    >
      <CommonLabel path="BWF/shop/productDetailPage/crossSelling/crossSellingCardBtn" />
      <SharedPrice :value="totalExchangeBoxPrice || unitPrice" />
    </AddToCartButton>
  </div>
</template>

<script setup>
import CommonLabel from "~/templates/components/CommonLabel.vue";
import ProductCrossSellingPrice from "~/templates/components/shop/product/ProductCrossSellingPrice.vue";
import AddToCartButton from "~/templates/partials/header/AddToCartButton.vue";
import ProductImage from "~/templates/elements/ProductImage.vue";
import { Tag } from "~/components/clarity-ui/Tag";
import {
  CTX_KEY_SUBSCRIPTION_ACTIVE,
  CTX_KEY_SUBSCRIPTION_ACTIVE_ID,
} from "~/composables/shop/useProductSubscriptionConfigurator";
import { getFirstTextOption } from "~/utils/helper/shop/product";
import useItemTracking from "~/composables/tracking/useItemTracking";
const props = defineProps(["crossSellingProduct", "subscriptionIndex"]);
const { getLabels } = useCommonLabels();

defineEmits(["close-cross-selling-modal"]);

const { product } = useProduct(props.crossSellingProduct);
const { persistTrackingItem } = useItemTracking({ item: product });

const subscriptionInterval = computed(() => {
  return product.value?.extensions?.subscription?.intervalConfigurations?.[props.subscriptionIndex];
});

const isExchangeBox = computed(
  () => product?.value.extensions?.hasOwnProperty("tauschbox") ?? false,
);

const exchangeBox = computed(() => product?.value.extensions?.tauschbox);
const cylinderAmount = computed(() => exchangeBox.value?.amountCylinders);
const exchangeBoxPrice = computed(() => exchangeBox.value?.tauschboxPrice);
const totalDepositPrice = computed(() => exchangeBox.value?.totalDepositPrice);
const totalExchangeBoxPrice = computed(() => exchangeBox.value?.totalTauschboxPrice);

const firstTextOption = getFirstTextOption(product?.value);

/*
 * This "intercepts" the injection chain as x selling product should not be mixed with the UI State of Product on the PDP
 * x selling products are pinned to a certain subscription if the interval is active.
 * provided values will end up here which will effectively scope the x selling from the main product.
 *
 * more:
 * composables/shop/useProductSubscriptionConfigurator.ts:71
 *
 * thoughts:
 * consider moving this manual / unresponsive ref to a custom composable
 *
 * */
const subscriptionChooserActive = ref(!!subscriptionInterval.value);
provide(CTX_KEY_SUBSCRIPTION_ACTIVE, subscriptionChooserActive);
const localInterval = ref(subscriptionInterval.value?.intervalConfigurationId);
provide(CTX_KEY_SUBSCRIPTION_ACTIVE_ID, localInterval);

const listPrice = computed(() => {
  return subscriptionInterval.value
    ? subscriptionInterval.value?.subscriptionPrice?.listPrice?.price
    : product.value?.calculatedPrice?.listPrice?.price;
});

const unitPrice = computed(() => {
  return subscriptionInterval.value
    ? subscriptionInterval.value?.subscriptionPrice?.totalPrice
    : product.value?.calculatedPrice?.unitPrice;
});

const intervalLabels = getLabels("BWF/shop/account/subscriptions/contract", [
  "days",
  "weeks",
  "months",
  "years",
]);

const intervalUnitLabel = intervalLabels[`${subscriptionInterval.value?.intervalUnit}`];

onMounted(() => {
  // no item select as there is no click / navigation before placing int into the cart
  persistTrackingItem();
});
</script>
